import { useEffect, useMemo, useState } from 'react'
import { useEditor } from '../store/useEditor'
import { loadProject, updateJobsCustomer, type ProjectSummary } from '../lib/db'
import { groupCustomers, STATE_LABEL, type CustomerRow, type CustomerState } from '../lib/customers'
import { JOB_STATUS } from '../types/document'
import { Empty, SaidLine, useSaid } from './bits'


/**
 * EVERY CUSTOMER, AND WHERE EACH ONE STANDS.
 *
 * The job list answers "which house was that". This answers the question a
 * season is actually run on: who has not answered me, who said yes, and what
 * is still out there. Those are two different screens because they are two
 * different questions, and one list trying to be both is a list that is
 * skimmed rather than read.
 *
 * Approved, not approved yet and not sent yet are read off the jobs, never
 * off a flag somebody has to remember to set. See lib/customers.ts.
 *
 * Editing a customer writes through to every job of theirs, because that is
 * what correcting a spelling means. It does not reprint anything: a quote that
 * has already gone out keeps saying what it said.
 *
 * THERE IS STILL NO CUSTOMER RECORD. A customer is the jobs carrying their
 * name, folded together. Nothing here creates an id, and nothing here should:
 * the day this becomes a table is the day a job can point at the wrong one.
 */

const money = (n: number) => `$${Math.round(n).toLocaleString()}`

const SEGMENTS: { id: 'all' | CustomerState; label: string }[] = [
  { id: 'all', label: 'Everyone' },
  { id: 'waiting', label: 'Not approved yet' },
  { id: 'approved', label: 'Approved' },
  { id: 'draft', label: 'Not sent yet' },
]

function Profile({
  row,
  onClose,
  onChanged,
  onOpenJob,
}: {
  row: CustomerRow
  onClose: () => void
  onChanged: () => void
  onOpenJob: (p: ProjectSummary) => void
}) {
  const [name, setName] = useState(row.name)
  const [address, setAddress] = useState(row.address)
  const [phone, setPhone] = useState(row.phone)
  const [editing, setEditing] = useState(false)
  const { said, say } = useSaid()

  const dirty = name !== row.name || address !== row.address || phone !== row.phone

  const save = async () => {
    const n = await updateJobsCustomer(
      row.jobs.map((j) => j.id),
      { customer: name, address, phone },
    )
    /*
     * A CUSTOMER IS THE JOBS THAT CARRY THEIR NAME, so correcting a spelling
     * writes to every one of them, and the sentence says so. With one job it
     * says Saved, because "saved to all 1 job" is a machine counting.
     */
    say(n === 1 ? 'Saved.' : `Saved to all ${n} jobs.`)
    setEditing(false)
    onChanged()
  }

  return (
    <>
      {/*
        THE NAME, AND NOT A DOCUMENT'S STATE BESIDE IT.

        There was a pill against the name reading "Not sent yet", which is
        what a quote is, not what a person is. It says the same thing about
        somebody with one unsent quote and somebody with nine, and the jobs
        listed six inches below it already say it per job, truthfully. It
        stays in the list, where a column of states is how you scan forty
        people; it comes off the record, where it was the loudest wrong thing
        on the screen.
      */}
      <div className="ws-head">
        <div>
          <h1>{row.name}</h1>
          <p>
            {row.jobs.length} {row.jobs.length === 1 ? 'job' : 'jobs'}
            {row.address.trim() ? `  ·  ${row.address.split('\n')[0]}` : ''}
          </p>
        </div>
        <div className="ws-head-acts">
          <button className="btn" onClick={onClose}>
            Back to everyone
          </button>
        </div>
      </div>

      {/* The same two columns as the job page, because it is the same kind of
          screen: the work down the middle, the facts in the rail. */}
      <div className="rec-grid">
        <div className="rec-main">
          <div className="ws-card">
            <p className="ws-eyebrow cust-jobs-head">
              {row.jobs.length === 1 ? 'Their job' : 'Their jobs'}
            </p>
            {row.jobs.map((p) => (
              <div key={p.id} className="ws-item">
                <div className="ws-row">
                  <button className="ws-open" onClick={() => onOpenJob(p)}>
                    <span className="ws-thumb">
                      {p.thumb ? <img src={p.thumb} alt="" /> : <i />}
                    </span>
                    <span className="ws-name">
                      <strong>
                        {p.money?.number || p.name}
                        {p.money?.kind === 'invoice' ? '  ·  Invoice' : ''}
                      </strong>
                      <span>
                        {new Date(p.savedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        {'  ·  '}
                        {JOB_STATUS.find((x) => x.id === p.status)?.label}
                      </span>
                    </span>
                    <span className="ws-amount">
                      {p.money && p.money.total > 0 ? money(p.money.total) : ''}
                      {p.money?.kind === 'invoice' && p.money.balance > 0 && (
                        <em>{money(p.money.balance)} due</em>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            ))}
            <p className="cust-note">
              Open a job to change its quote, turn it into an invoice, or record a deposit.
            </p>
          </div>
        </div>

        <aside className="rec-rail">
          {/*
            WHAT THEY ARE WORTH, in rows. It was one figure tile 220 pixels
            tall holding the word Quoted and a dollar sign, across the whole
            width of the screen.
          */}
          <div className="ws-card rec-figs">
            <dl>
              <div>
                <dt>Quoted</dt>
                <dd>{money(row.quoted)}</dd>
              </div>
              {row.collected > 0 && (
                <div>
                  <dt>Collected</dt>
                  <dd>{money(row.collected)}</dd>
                </div>
              )}
              {row.owed > 0 && (
                <div>
                  <dt>Still owed</dt>
                  <dd>
                    {money(row.owed)}
                    <em>Invoiced and not paid</em>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="ws-card rec-who">
            <div className="rec-who-head">
              <p className="insp-lbl plain">Their details</p>
              {!editing && (
                <button className="link-btn" onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
            </div>

            {editing ? (
              <div className="ws-form rec-who-form">
                <label className="ws-wide">
                  <span>Name</span>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="ws-wide">
                  <span>Phone</span>
                  <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </label>
                <label className="ws-wide">
                  <span>Address</span>
                  <textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
                </label>
                <p className="hint ws-wide">
                  {row.jobs.length === 1
                    ? 'This writes to their job.'
                    : `This writes to all ${row.jobs.length} of their jobs.`}
                </p>
                <div className="ws-wide ws-actions">
                  <SaidLine said={said} />
                  <button
                    className="btn ghost"
                    onClick={() => {
                      setName(row.name)
                      setAddress(row.address)
                      setPhone(row.phone)
                      setEditing(false)
                    }}
                  >
                    Cancel
                  </button>
                  <button className="btn primary" onClick={() => void save()} disabled={!dirty}>
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <dl className="rec-contact">
                <div>
                  <dt>Name</dt>
                  <dd>{row.name}</dd>
                </div>
                {row.address.trim() && (
                  <div>
                    <dt>Address</dt>
                    <dd>{row.address}</dd>
                  </div>
                )}
                {row.phone.trim() && (
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${row.phone.replace(/[^0-9+]/g, '')}`}>{row.phone}</a>
                    </dd>
                  </div>
                )}
                <SaidLine said={said} />
              </dl>
            )}
          </div>
        </aside>
      </div>
    </>
  )
}

export default function Customers({
  items,
  onOpened,
  onChanged,
  onRecord,
}: {
  items: ProjectSummary[]
  onOpened: () => void
  onChanged: () => void
  /*
   * A RECORD IS NOT A THING INSIDE A LIST.
   *
   * Opening a customer rendered their record underneath the list's own
   * heading, so the screen read Who is waiting on you, then the customer's
   * name, with the List and Board switch still sitting at the top right of a
   * page that was neither a list nor a board. Two page titles, and a control
   * for a view you had already left.
   *
   * The list tells the workspace when it is showing a record, and the
   * workspace stands its heading down.
   */
  onRecord?: (open: boolean) => void
}) {
  const [q, setQ] = useState('')
  const [seg, setSeg] = useState<'all' | CustomerState>('all')
  const [openKey, setOpenKey] = useState<string | null>(null)
  const loadDocument = useEditor((s) => s.loadDocument)

  const rows = useMemo(() => groupCustomers(items), [items])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return rows
      .filter((r) => seg === 'all' || r.state === seg)
      .filter((r) =>
        needle ? `${r.name} ${r.address} ${r.phone}`.toLowerCase().includes(needle) : true,
      )
  }, [rows, q, seg])

  const openJob = async (p: ProjectSummary) => {
    const doc = await loadProject(p.id)
    if (doc) {
      loadDocument(doc)
      onOpened()
    }
  }

  const open = rows.find((r) => r.key === openKey)
  /*
    In an effect, not in the render. Telling a parent to set state while this
    component is still rendering is how you get Cannot update a component
    while rendering a different component, and the warning is right: the
    parent would be re-rendering mid pass.
  */
  useEffect(() => {
    onRecord?.(!!open)
    return () => onRecord?.(false)
  }, [open, onRecord])
  if (open) {
    return (
      <Profile
        row={open}
        onClose={() => setOpenKey(null)}
        onChanged={onChanged}
        onOpenJob={openJob}
      />
    )
  }

  return (
    <>
      <div className="ws-tools">
        <div className="ws-tabs">
          {SEGMENTS.map((s) => (
            <button key={s.id} aria-pressed={seg === s.id} onClick={() => setSeg(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        <input
          className="ws-search"
          type="search"
          value={q}
          placeholder="A name, a street, a phone number"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {shown.length === 0 ? (
        <div className="ws-card">
          {rows.length === 0 ? (
            <Empty title="Nobody yet">
              A job becomes a customer as soon as you put a name on it.
            </Empty>
          ) : (
            <Empty kind="filtered" action={{ label: 'Clear the search', onClick: () => setQ('') }}>
              Nobody matches that.
            </Empty>
          )}
        </div>
      ) : (

        <div className="ws-card">
          {shown.map((r) => (
            <div key={r.key} className="ws-item">
              <div className="ws-row">
                <button className="ws-open nothumb" onClick={() => setOpenKey(r.key)}>
                  <span className="ws-name">
                    <strong>{r.name}</strong>
                    <span>
                      {[r.address.split('\n')[0], r.phone]
                        .filter((x) => x && x.trim())
                        .join('  ·  ')}
                    </span>
                  </span>
                  <span className={`ws-pill ${r.state}`}>{STATE_LABEL[r.state]}</span>
                  <span className="ws-amount">
                    {r.quoted > 0 ? money(r.quoted) : ''}
                    {r.jobs.length > 1 && <em>{r.jobs.length} jobs</em>}
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
